import { useCallback, useEffect, useRef, useState } from 'react';
import { debtService } from '../../../services/debtService';
import type { CreatePaymentRequest, DebtDirection, DebtEntry, UpdatePaymentRequest } from '../../../types/debt';

const PAGE_SIZE = 20;

export function useDebtLedger(contactId: number, direction: DebtDirection) {
  const [entries, setEntries] = useState<DebtEntry[]>([]);
  const [balance, setBalance] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Monotonic request id: stale or post-unmount responses are ignored
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const [ledgerPage, currentBalance] = await Promise.all([
        debtService.getContactLedger(contactId, direction, page, PAGE_SIZE),
        debtService.getBalance(contactId, direction),
      ]);
      if (isStale()) return;
      setEntries(ledgerPage.content);
      setTotalPages(ledgerPage.totalPages);
      setBalance(currentBalance);
    } catch (err) {
      console.error('Failed to load debt ledger:', err);
      if (!isStale()) {
        setError('Не вдалося завантажити історію боргу');
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [contactId, direction, page]);

  useEffect(() => {
    reload();
    return () => {
      // Invalidate any in-flight request on unmount / param change
      requestIdRef.current++;
    };
  }, [reload]);

  useEffect(() => {
    setPage(0);
  }, [direction]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const recordPayment = async (request: CreatePaymentRequest) => {
    await debtService.recordPayment(request);
    await reload();
  };

  const updatePayment = async (paymentId: number, request: UpdatePaymentRequest) => {
    await debtService.updatePayment(paymentId, request);
    await reload();
  };

  const deletePayment = async (paymentId: number) => {
    await debtService.deletePayment(paymentId);
    await reload();
  };

  return {
    data: {
      entries,
      balance,
      loading,
      error,
    },
    pagination: {
      page,
      count: totalPages,
      onChange: handlePageChange,
    },
    actions: {
      recordPayment,
      updatePayment,
      deletePayment,
    },
  };
}
